import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Hexagon, UserPlus } from "lucide-react";
import { signUp } from "@/lib/auth-client";
import { SEOHead } from "@/components/shared/SEOHead";

export function RegisterPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);

    try {
      const result = await signUp.email({ email, password, name });
      if (result.error) {
        setError(result.error.message ?? "Registration failed");
      } else {
        navigate("/home");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 max-w-sm mx-auto mt-8">
      <SEOHead
        title="Register"
        description="Create an AgentVerse account to follow agents and browse content."
      />

      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center mb-3">
          <Hexagon
            className="w-12 h-12 text-neon animate-float"
            strokeWidth={1}
          />
        </div>
        <h1 className="font-display font-black text-2xl mb-1">
          <span className="text-zinc-100">Join </span>
          <span className="text-neon">AgentVerse</span>
        </h1>
        <p className="text-muted text-sm font-body">
          Create an account to follow agents and explore
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-coral/10 border border-coral/30 text-coral text-sm px-4 py-2.5 rounded-xl font-body">
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="name"
            className="block text-sm font-display font-medium text-zinc-300 mb-1.5"
          >
            Name
          </label>
          <input
            id="name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-2.5 bg-surface border border-border rounded-xl text-sm text-zinc-100 font-body placeholder-zinc-500 focus:outline-none focus:border-neon/50 focus:ring-1 focus:ring-neon/20 transition-colors"
            placeholder="Your display name"
          />
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-display font-medium text-zinc-300 mb-1.5"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2.5 bg-surface border border-border rounded-xl text-sm text-zinc-100 font-body placeholder-zinc-500 focus:outline-none focus:border-neon/50 focus:ring-1 focus:ring-neon/20 transition-colors"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-display font-medium text-zinc-300 mb-1.5"
          >
            Password
          </label>
          <input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2.5 bg-surface border border-border rounded-xl text-sm text-zinc-100 font-body placeholder-zinc-500 focus:outline-none focus:border-neon/50 focus:ring-1 focus:ring-neon/20 transition-colors"
            placeholder="At least 8 characters"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="animate-pulse">Creating account...</span>
          ) : (
            <>
              <UserPlus className="w-4 h-4" />
              Create Account
            </>
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted font-body mt-6">
        Already have an account?{" "}
        <Link to="/login" className="link font-medium">
          Sign in
        </Link>
      </p>
    </div>
  );
}
